import * as Form from "@radix-ui/react-form";
import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { login } from "@/api";
import { Button, ColumnModal, Input } from "@/components";
import { Hide, Show } from "@/icons";
import { useAuthToken, useTranslate } from "@/providers/context";

import { UserScreenWrapper } from "./components/UserScreenWrapper.tsx";
import { useResetPassword } from "./hooks/useResetPassword.tsx";

export const SignInScreen = () => {
  const [email, setEmail] = useState<string>("");
  const [pass, setPass] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<boolean>(false); // ResetPassword Modal
  const navigate = useNavigate();
  const { setToken } = useAuthToken();
  const { translate } = useTranslate();
  const [searchParams] = useSearchParams();
  const resetPassword = useResetPassword();

  const redirect = searchParams.get("redirect");

  const signIn = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await login(email, pass);
      if (token) {
        setToken(token);
        if (redirect) {
          navigate(redirect, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } else {
        setError("Failed to sign in, please try again later");
      }
    } catch (e) {
      let message = "Failed to sign in, please try again later";

      if (e instanceof Error && e.message) {
        message = e.message;
      }
      console.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [email, pass, setToken, redirect, navigate, setLoading]);
  const togglePassword = useCallback(() => {
    setVisible((v) => !v);
  }, [setVisible]);

  return (
    <UserScreenWrapper showCloseButton contentClassName="pb-[120px] sm:pb-0">
      <div className="text-item-primary">
        <h1 className="h1 mb-[12px]">{translate("Sign in")}</h1>
        {redirect ? (
          <h5 className="p1-regular sm:h5-regular mb-7 sm:mb-8">
            {translate("You do not have access to this content.")}{" "}
            {translate("Sign in or")}{" "}
            <span
              className="p1-semibold sm:h5-semibold cursor-pointer text-highlightPrimary"
              onClick={() =>
                navigate(
                  redirect ? `/sign-up?redirect=${redirect}` : "/sign-up",
                  { replace: true },
                )
              }
            >
              {translate("create an account")}
            </span>{" "}
            {translate("to continue.")}
          </h5>
        ) : (
          <h5 className="p1-regular sm:h5-regular mb-7 sm:mb-8">
            {translate("Don't have an account yet?")}{" "}
            <span
              className="p1-semibold sm:h5-semibold cursor-pointer text-highlightPrimary"
              onClick={() => navigate("/sign-up", { replace: true })}
            >
              {translate("Sign up now")}
            </span>
          </h5>
        )}
      </div>
      <Form.Root
        data-testid="sign-in-form"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form.Field name="email">
          <div>
            <Form.Message match="valueMissing">
              {translate("Email")}
            </Form.Message>
          </div>
          <Form.Control asChild>
            <Input
              className="mb-[52px] sm:mb-11"
              autoComplete="email"
              type="email"
              placeholder={translate("Email")}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Control>
        </Form.Field>
        <Form.Field name="password" className="mb-[44px] w-full">
          <div>
            <Form.Message match="valueMissing">
              {translate("Password")}
            </Form.Message>
          </div>
          <div className="relative flex items-center">
            <Form.Control asChild>
              <Input
                className="pr-[50px]"
                autoComplete="current-password"
                type={visible ? "text" : "password"}
                placeholder={translate("Password")}
                onChange={(e) => setPass(e.target.value)}
              />
            </Form.Control>
            <div className="absolute right-[20px] flex h-full items-center ">
              {visible ? (
                <Hide
                  className="h-[20px] w-[20px] cursor-pointer text-item-secondary hover:text-item-primary"
                  onClick={togglePassword}
                />
              ) : (
                <Show
                  className="h-[20px] w-[20px] cursor-pointer text-item-secondary hover:text-item-primary"
                  onClick={togglePassword}
                />
              )}
            </div>
          </div>
          <Form.Message
            match="typeMismatch"
            forceMatch={!!error}
            className="block h-0 pt-[16px] text-error-item"
          >
            {translate(error)}
          </Form.Message>
        </Form.Field>
        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-center border-t border-border bg-surface-primary px-5 py-4 sm:relative sm:border-none sm:p-0">
          <Form.Submit asChild>
            <Button
              size="l"
              className="text-md h-fit w-full max-w-[440px] px-[14px] py-[10px] sm:mt-[8px] sm:text-sm"
              disabled={loading}
              loading={loading}
              onClick={signIn}
            >
              {translate("Continue")}
            </Button>
          </Form.Submit>
          <div className="flex pt-4 sm:pt-6">
            <ColumnModal
              title={translate("Reset password")}
              titleClassName="capitalize"
              open={open}
              onCancel={() => setOpen(false)}
              cols={3}
              confirmIsLoading={resetPassword.isLoading}
              confirmDisabled={resetPassword.isLoading}
              onConfirm={() => {
                resetPassword.mutate(email, {
                  onSettled: () => {
                    setOpen(false);
                  },
                });
              }}
              primaryText={translate("Send Email")}
              triggerButton={
                <div
                  className="p3-medium sm:p2-medium cursor-pointer capitalize text-item-secondary sm:mb-12"
                  onClick={() => setOpen(true)}
                >
                  {translate("Reset password")}
                </div>
              }
            >
              <div className="p1-regular px-[24px]">
                {translate(
                  "We will email you a link to reset your password. If you don't see it, please check your spam folder.",
                )}
                <Input
                  className="my-[24px]"
                  type="email"
                  placeholder={translate("Email address")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </ColumnModal>
          </div>
        </div>
      </Form.Root>
    </UserScreenWrapper>
  );
};
